import { accessProvider } from '@/access';
import { toApiRequestError } from './errors';
import type {
  AdminApiCredential,
  AdminPaymentIntentReconcileResult,
  AdminAuditLogFacetField,
  AdminAuditLogFacetFilters,
  AdminAuditLogFacetsResponse,
  AdminAuditLogFilters,
  AdminAuditLogListResponse,
  AdminApiCredentialAllowedTenantsUpdateRequest,
  AdminApiCredentialCreateRequest,
  AdminApiCredentialIpAllowlistUpdateRequest,
  AdminApiCredentialListFilters,
  AdminApiCredentialListResponse,
  AdminApiCredentialRevokeRequest,
  AdminApplication,
  AdminApplicationCreateRequest,
  AdminApplicationDeclaredEgressIpsUpdateRequest,
  AdminApplicationEdgeVerification,
  AdminApplicationEgressIpsResponse,
  AdminApplicationListResponse,
  AdminApplicationUpdateRequest,
  AdminGatewayAccount,
  AdminGatewayAccountCreateRequest,
  AdminGatewayAccountListFilters,
  AdminGatewayAccountListResponse,
  AdminGatewayAccountSecretsResponse,
  AdminGatewayAccountUpdateRequest,
  AdminGatewayDescriptorListResponse,
  AdminTenant,
  AdminTenantCreateRequest,
  AdminTenantListFilters,
  AdminTenantListResponse,
  AdminTenantPolicy,
  AdminTenantPolicyUpdateRequest,
  AdminTenantStatusUpdateRequest,
  AdminTenantTagsResponse,
  AdminTenantTagsUpdateRequest,
  AdminTenantUpdateRequest,
  GatewayFieldsWriteRequest,
  IssuedApiCredentialMaterial,
  PaymentIntent,
  Problem,
  ReportAggregatesFilters,
  ReportAggregatesResponse,
  ReportOperationsFilters,
  ReportOperationsListResponse,
  TestBenchPaymentLaunchRequest,
  TestBenchPaymentLaunchResponse,
  TestBenchPaymentListResponse,
  TestBenchPaymentTimeline,
  TestBenchPreset,
  TestBenchPresetLinkRequest,
  TestBenchPresetLinkResponse,
  TestBenchStatus,
} from './models';

const baseUrl = import.meta.env.VITE_API_BASE_URL;

function appendArrayParam(params: URLSearchParams, key: string, values: string[] | undefined): void {
  if (!values) return;
  for (const value of values) params.append(key, value);
}

function buildOperationsQuery(filters: ReportOperationsFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  appendArrayParam(params, 'tenantCode', filters.tenantCode);
  appendArrayParam(params, 'tenantTag', filters.tenantTag);
  appendArrayParam(params, 'applicationCode', filters.applicationCode);
  appendArrayParam(params, 'gateway', filters.gateway);
  appendArrayParam(params, 'status', filters.status);
  appendArrayParam(params, 'type', filters.type);
  if (filters.amountMin !== undefined) params.set('amountMin', String(filters.amountMin));
  if (filters.amountMax !== undefined) params.set('amountMax', String(filters.amountMax));
  if (filters.orderReference) params.set('orderReference', filters.orderReference);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  if (filters.sort) params.set('sort', filters.sort);
  return params;
}

function buildAdminTenantsQuery(filters: AdminTenantListFilters): URLSearchParams {
  const params = new URLSearchParams();
  appendArrayParam(params, 'status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  return params;
}

function buildAdminApiCredentialsQuery(filters: AdminApiCredentialListFilters): URLSearchParams {
  const params = new URLSearchParams();
  appendArrayParam(params, 'applicationCode', filters.applicationCode);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  return params;
}

function buildAdminGatewayAccountsQuery(filters: AdminGatewayAccountListFilters): URLSearchParams {
  const params = new URLSearchParams();
  appendArrayParam(params, 'tenantCode', filters.tenantCode);
  appendArrayParam(params, 'gateway', filters.gateway);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  return params;
}

function buildAdminAuditLogQuery(filters: AdminAuditLogFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.actor) params.set('actor', filters.actor);
  appendArrayParam(params, 'actorType', filters.actorType);
  appendArrayParam(params, 'action', filters.action);
  if (filters.objectType) params.set('objectType', filters.objectType);
  if (filters.objectId) params.set('objectId', filters.objectId);
  appendArrayParam(params, 'tenantCode', filters.tenantCode);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.pageSize !== undefined) params.set('pageSize', String(filters.pageSize));
  return params;
}

function buildAuditLogFacetsQuery(field: AdminAuditLogFacetField, filters: AdminAuditLogFacetFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('field', field);
  appendArrayParam(params, 'actorType', filters.actorType);
  if (filters.objectType) params.set('objectType', filters.objectType);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  return params;
}

function buildPageQuery(page?: number, pageSize?: number): URLSearchParams {
  const params = new URLSearchParams();
  if (page !== undefined) params.set('page', String(page));
  if (pageSize !== undefined) params.set('pageSize', String(pageSize));
  return params;
}

function buildAggregatesQuery(filters: ReportAggregatesFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  appendArrayParam(params, 'tenantCode', filters.tenantCode);
  appendArrayParam(params, 'tenantTag', filters.tenantTag);
  appendArrayParam(params, 'applicationCode', filters.applicationCode);
  appendArrayParam(params, 'gateway', filters.gateway);
  appendArrayParam(params, 'groupBy', filters.groupBy);
  if (filters.period) params.set('period', filters.period);
  return params;
}

/**
 * Thin, typed fetch wrapper. Every path this client calls — including the
 * reports surface — lives under `/v1/admin/*` (the administration reports
 * surface, contract 1.5.0.0), because `sessionAuth` never authenticates a
 * path outside `/v1/admin/*`: the credential-authenticated `/v1/reports/*`
 * twin belongs to calling applications holding a server-to-server signing
 * secret, which an operator's browser session must never hold.
 */
async function request<T>(path: string, params?: URLSearchParams): Promise<T> {
  const token = await accessProvider.getSessionToken();
  const query = params && params.toString().length > 0 ? `?${params.toString()}` : '';
  const response = await fetch(`${baseUrl}${path}${query}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let problem: Partial<Problem> | undefined;
    try {
      problem = (await response.json()) as Partial<Problem>;
    } catch {
      problem = undefined;
    }
    throw toApiRequestError(response.status, problem);
  }

  return (await response.json()) as T;
}

async function requestBlob(path: string, params?: URLSearchParams): Promise<Blob> {
  const token = await accessProvider.getSessionToken();
  const query = params && params.toString().length > 0 ? `?${params.toString()}` : '';
  const response = await fetch(`${baseUrl}${path}${query}`, {
    headers: {
      Accept: 'text/csv',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let problem: Partial<Problem> | undefined;
    try {
      problem = (await response.json()) as Partial<Problem>;
    } catch {
      problem = undefined;
    }
    throw toApiRequestError(response.status, problem);
  }

  return await response.blob();
}

/**
 * Sends a JSON body to a mutating administration operation. Every
 * administration path is `sessionAuth`-only (plan D-M) — the same bearer
 * session token used by `request`/`requestBlob` above, since this browser
 * session never holds a server-to-server `ApiCredential` secret (see the
 * repo's Hard Constraints). Kept as its own helper, and the endpoints below
 * grouped and named after the contract's own `operationId`s, so the
 * administration surface reads as a distinct block from the reports surface
 * rather than a blurred, undifferentiated set of calls.
 */
async function requestWithBody<T>(method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, body: unknown): Promise<T> {
  const token = await accessProvider.getSessionToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let problem: Partial<Problem> | undefined;
    try {
      problem = (await response.json()) as Partial<Problem>;
    } catch {
      problem = undefined;
    }
    throw toApiRequestError(response.status, problem);
  }

  return (await response.json()) as T;
}

/**
 * `requestWithBody`'s counterpart for the operations whose success is `204 No
 * Content`. Separate rather than a flag on that helper because the difference
 * is in the return type, not in a parameter: parsing an empty body as JSON
 * throws, so a shared implementation would have to lie about what it returns.
 * The failure path is identical — a `problem+json` body is still read and
 * turned into an `ApiRequestError`.
 */
async function requestNoContent(method: 'POST' | 'DELETE', path: string): Promise<void> {
  const token = await accessProvider.getSessionToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let problem: Partial<Problem> | undefined;
    try {
      problem = (await response.json()) as Partial<Problem>;
    } catch {
      problem = undefined;
    }
    throw toApiRequestError(response.status, problem);
  }
}

export const payApiClient = {
  listReportOperations(filters: ReportOperationsFilters): Promise<ReportOperationsListResponse> {
    return request<ReportOperationsListResponse>(
      '/v1/admin/reports/operations',
      buildOperationsQuery(filters),
    );
  },

  getReportOperationDetail(id: string): Promise<PaymentIntent> {
    return request<PaymentIntent>(`/v1/admin/reports/operations/${encodeURIComponent(id)}`);
  },

  /** `POST /v1/admin/payment-intents/{id}/reconcile` — idempotent, no request body. */
  reconcilePaymentIntent(id: string): Promise<AdminPaymentIntentReconcileResult> {
    return requestWithBody<AdminPaymentIntentReconcileResult>(
      'POST',
      `/v1/admin/payment-intents/${encodeURIComponent(id)}/reconcile`,
      {},
    );
  },

  getReportAggregates(filters: ReportAggregatesFilters): Promise<ReportAggregatesResponse> {
    return request<ReportAggregatesResponse>('/v1/admin/reports/aggregates', buildAggregatesQuery(filters));
  },

  exportReportOperations(filters: ReportOperationsFilters): Promise<Blob> {
    return requestBlob('/v1/admin/reports/operations/export', buildOperationsQuery(filters));
  },

  // --- Administration: tenants (`sessionAuth`, plan D-M / A2 contract) ---

  listAdminTenants(filters: AdminTenantListFilters): Promise<AdminTenantListResponse> {
    return request<AdminTenantListResponse>('/v1/admin/tenants', buildAdminTenantsQuery(filters));
  },

  createAdminTenant(body: AdminTenantCreateRequest): Promise<AdminTenant> {
    return requestWithBody<AdminTenant>('POST', '/v1/admin/tenants', body);
  },

  getAdminTenant(code: string): Promise<AdminTenant> {
    return request<AdminTenant>(`/v1/admin/tenants/${encodeURIComponent(code)}`);
  },

  updateAdminTenant(code: string, body: AdminTenantUpdateRequest): Promise<AdminTenant> {
    return requestWithBody<AdminTenant>('PATCH', `/v1/admin/tenants/${encodeURIComponent(code)}`, body);
  },

  updateAdminTenantStatus(code: string, body: AdminTenantStatusUpdateRequest): Promise<AdminTenant> {
    return requestWithBody<AdminTenant>('PUT', `/v1/admin/tenants/${encodeURIComponent(code)}/status`, body);
  },

  updateAdminTenantTags(code: string, body: AdminTenantTagsUpdateRequest): Promise<AdminTenantTagsResponse> {
    return requestWithBody<AdminTenantTagsResponse>('PUT', `/v1/admin/tenants/${encodeURIComponent(code)}/tags`, body);
  },

  getAdminTenantPolicy(code: string): Promise<AdminTenantPolicy> {
    return request<AdminTenantPolicy>(`/v1/admin/tenants/${encodeURIComponent(code)}/policy`);
  },

  updateAdminTenantPolicy(code: string, body: AdminTenantPolicyUpdateRequest): Promise<AdminTenantPolicy> {
    return requestWithBody<AdminTenantPolicy>('PUT', `/v1/admin/tenants/${encodeURIComponent(code)}/policy`, body);
  },

  // --- Administration: applications (`sessionAuth`, plan D-M / A3 contract) ---

  listAdminApplications(page?: number, pageSize?: number): Promise<AdminApplicationListResponse> {
    return request<AdminApplicationListResponse>('/v1/admin/applications', buildPageQuery(page, pageSize));
  },

  createAdminApplication(body: AdminApplicationCreateRequest): Promise<AdminApplication> {
    return requestWithBody<AdminApplication>('POST', '/v1/admin/applications', body);
  },

  getAdminApplication(code: string): Promise<AdminApplication> {
    return request<AdminApplication>(`/v1/admin/applications/${encodeURIComponent(code)}`);
  },

  updateAdminApplication(code: string, body: AdminApplicationUpdateRequest): Promise<AdminApplication> {
    return requestWithBody<AdminApplication>('PATCH', `/v1/admin/applications/${encodeURIComponent(code)}`, body);
  },

  getApplicationEgressIps(id: string): Promise<AdminApplicationEgressIpsResponse> {
    return request<AdminApplicationEgressIpsResponse>(`/v1/admin/applications/${encodeURIComponent(id)}/egress-ips`);
  },

  /** Replaces the declared set in full. Documentation only — the platform never filters calls by these. */
  updateApplicationDeclaredEgressIps(
    id: string,
    body: AdminApplicationDeclaredEgressIpsUpdateRequest,
  ): Promise<AdminApplicationEgressIpsResponse> {
    return requestWithBody<AdminApplicationEgressIpsResponse>(
      'PUT',
      `/v1/admin/applications/${encodeURIComponent(id)}/declared-egress-ips`,
      body,
    );
  },

  getApplicationEdgeVerification(id: string): Promise<AdminApplicationEdgeVerification> {
    return request<AdminApplicationEdgeVerification>(`/v1/admin/applications/${encodeURIComponent(id)}/edge-verification`);
  },

  // --- Administration: API credentials (`sessionAuth`, plan D-M / D-N / A3 contract) ---

  listAdminApiCredentials(filters: AdminApiCredentialListFilters): Promise<AdminApiCredentialListResponse> {
    return request<AdminApiCredentialListResponse>('/v1/admin/api-credentials', buildAdminApiCredentialsQuery(filters));
  },

  /** `secret`/`signingSecret` are present in this response exactly once (D-N) — never call this to "peek". */
  createAdminApiCredential(body: AdminApiCredentialCreateRequest): Promise<IssuedApiCredentialMaterial> {
    return requestWithBody<IssuedApiCredentialMaterial>('POST', '/v1/admin/api-credentials', body);
  },

  getAdminApiCredential(id: string): Promise<AdminApiCredential> {
    return request<AdminApiCredential>(`/v1/admin/api-credentials/${encodeURIComponent(id)}`);
  },

  revokeAdminApiCredential(id: string, body: AdminApiCredentialRevokeRequest): Promise<AdminApiCredential> {
    return requestWithBody<AdminApiCredential>('DELETE', `/v1/admin/api-credentials/${encodeURIComponent(id)}`, body);
  },

  /** Generates fresh `secret`/`signingSecret` for the same `id`; the previous material stops working immediately. */
  rotateAdminApiCredential(id: string): Promise<IssuedApiCredentialMaterial> {
    return requestWithBody<IssuedApiCredentialMaterial>('POST', `/v1/admin/api-credentials/${encodeURIComponent(id)}/rotate`, {});
  },

  updateAdminApiCredentialAllowedTenants(
    id: string,
    body: AdminApiCredentialAllowedTenantsUpdateRequest,
  ): Promise<AdminApiCredential> {
    return requestWithBody<AdminApiCredential>(
      'PUT',
      `/v1/admin/api-credentials/${encodeURIComponent(id)}/allowed-tenants`,
      body,
    );
  },

  updateAdminApiCredentialIpAllowlist(
    id: string,
    body: AdminApiCredentialIpAllowlistUpdateRequest,
  ): Promise<AdminApiCredential> {
    return requestWithBody<AdminApiCredential>(
      'PUT',
      `/v1/admin/api-credentials/${encodeURIComponent(id)}/ip-allowlist`,
      body,
    );
  },

  // --- Administration: gateway accounts (`sessionAuth`, plan D-M / A3 contract) ---

  /**
   * What each gateway needs configured — account fields and credential field names,
   * with the encoding each credential's value must be written in. Static per
   * gateway (no tenant, no account, no credential material), which is why it reads
   * without the role that guards credential writing.
   */
  listAdminGatewayDescriptors(): Promise<AdminGatewayDescriptorListResponse> {
    return request<AdminGatewayDescriptorListResponse>('/v1/admin/gateways');
  },

  listAdminGatewayAccounts(filters: AdminGatewayAccountListFilters): Promise<AdminGatewayAccountListResponse> {
    return request<AdminGatewayAccountListResponse>('/v1/admin/gateway-accounts', buildAdminGatewayAccountsQuery(filters));
  },

  createAdminGatewayAccount(body: AdminGatewayAccountCreateRequest): Promise<AdminGatewayAccount> {
    return requestWithBody<AdminGatewayAccount>('POST', '/v1/admin/gateway-accounts', body);
  },

  getAdminGatewayAccount(id: string): Promise<AdminGatewayAccount> {
    return request<AdminGatewayAccount>(`/v1/admin/gateway-accounts/${encodeURIComponent(id)}`);
  },

  updateAdminGatewayAccount(id: string, body: AdminGatewayAccountUpdateRequest): Promise<AdminGatewayAccount> {
    return requestWithBody<AdminGatewayAccount>('PATCH', `/v1/admin/gateway-accounts/${encodeURIComponent(id)}`, body);
  },

  /**
   * Discards an account no payment intent has used, with its credential fields
   * and its key. The platform refuses (`409`) as soon as the account has been
   * used — a used account is retired by setting its status to `suspended`.
   */
  deleteAdminGatewayAccount(id: string): Promise<void> {
    return requestNoContent('DELETE', `/v1/admin/gateway-accounts/${encodeURIComponent(id)}`);
  },

  /** Metadata only (D-M) — field name, whether set, last 4 where tolerated, fingerprint prefix, rotation date. Never a value. */
  getAdminGatewayAccountSecrets(id: string): Promise<AdminGatewayAccountSecretsResponse> {
    return request<AdminGatewayAccountSecretsResponse>(`/v1/admin/gateway-accounts/${encodeURIComponent(id)}/secrets`);
  },

  /** Write-only: accepts plaintext, returns the same metadata-only shape as the `GET` above, never the value back. */
  writeAdminGatewayAccountSecrets(id: string, body: GatewayFieldsWriteRequest): Promise<AdminGatewayAccountSecretsResponse> {
    return requestWithBody<AdminGatewayAccountSecretsResponse>(
      'PUT',
      `/v1/admin/gateway-accounts/${encodeURIComponent(id)}/secrets`,
      body,
    );
  },

  /**
   * Destroys every stored version of one credential field. The platform only
   * permits this for a field the account's gateway does not declare — one it
   * does read is replaced by writing it again, which rotates it. Returns the
   * same metadata-only shape as the other secrets operations.
   */
  deleteAdminGatewayAccountSecretField(id: string, fieldName: string): Promise<AdminGatewayAccountSecretsResponse> {
    return requestWithBody<AdminGatewayAccountSecretsResponse>(
      'DELETE',
      `/v1/admin/gateway-accounts/${encodeURIComponent(id)}/secrets/${encodeURIComponent(fieldName)}`,
      undefined,
    );
  },

  // --- Administration: audit trail (`sessionAuth`, plan D-M / A3 contract) ---
  // Both `platform:admin` and `platform:auditor` may call these — the auditor
  // is a first-class reader here, not a degraded one; there is nothing to
  // write on this surface (queryAdminAuditLog/exportAdminAuditLog).

  listAdminAuditLog(filters: AdminAuditLogFilters): Promise<AdminAuditLogListResponse> {
    return request<AdminAuditLogListResponse>('/v1/admin/audit-log', buildAdminAuditLogQuery(filters));
  },

  /** CSV export carrying the same filters as the list above — the file the API returns, never built client-side. */
  exportAdminAuditLog(filters: AdminAuditLogFilters): Promise<Blob> {
    return requestBlob('/v1/admin/audit-log/export', buildAdminAuditLogQuery(filters));
  },

  /**
   * `GET /v1/admin/audit-logs/facets` — the distinct values of one audit-log
   * column, for the filter comboboxes. Values arrive sorted and capped at
   * 1000 server-side, which is why the actor/objectId selects still accept
   * free text on top of what this returns.
   */
  getAuditLogFacets(field: AdminAuditLogFacetField, filters: AdminAuditLogFacetFilters = {}): Promise<AdminAuditLogFacetsResponse> {
    return request<AdminAuditLogFacetsResponse>('/v1/admin/audit-logs/facets', buildAuditLogFacetsQuery(field, filters));
  },

  // --- Administration: test bench (`sessionAuth`, run-6's fixed contract) ---
  // Every operation below 404s when the `TestBench` flag is disabled server
  // side — `useTestBenchAvailability` is the single place that probes
  // `getTestBenchStatus` and decides visibility; callers elsewhere assume
  // availability is already known.

  getTestBenchStatus(): Promise<TestBenchStatus> {
    return request<TestBenchStatus>('/v1/admin/test-bench/status');
  },

  getTestBenchPresets(): Promise<TestBenchPreset[]> {
    return request<TestBenchPreset[]>('/v1/admin/test-bench/presets');
  },

  linkTestBenchPreset(presetCode: string, body: TestBenchPresetLinkRequest): Promise<TestBenchPresetLinkResponse> {
    return requestWithBody<TestBenchPresetLinkResponse>(
      'POST',
      `/v1/admin/test-bench/presets/${encodeURIComponent(presetCode)}/link`,
      body,
    );
  },

  launchTestBenchPayment(body: TestBenchPaymentLaunchRequest): Promise<TestBenchPaymentLaunchResponse> {
    return requestWithBody<TestBenchPaymentLaunchResponse>('POST', '/v1/admin/test-bench/payments', body);
  },

  listTestBenchPayments(limit = 20): Promise<TestBenchPaymentListResponse> {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    return request<TestBenchPaymentListResponse>('/v1/admin/test-bench/payments', params);
  },

  getTestBenchPaymentTimeline(intentId: string): Promise<TestBenchPaymentTimeline> {
    return request<TestBenchPaymentTimeline>(`/v1/admin/test-bench/payments/${encodeURIComponent(intentId)}/timeline`);
  },
};
